import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { GUIDE_ARTICLES } from './guideArticlesData';
import { BLOG_SLUG_FREE_WEBSITE_UK, internalUrl, SITE_ORIGIN } from './siteConfig';

function GuidesHubPage() {
  useEffect(() => {
    document.title = 'Guides — SEO, accessibility & web strategy | WebCodeCraft';
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute('content');
    meta?.setAttribute(
      'content',
      'Practical guides on SEO, AEO, accessibility, performance, and choosing the right web approach — WebCodeCraft, UK.'
    );
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanon = canonical?.getAttribute('href');
    canonical?.setAttribute('href', `${SITE_ORIGIN}/guides`);
    return () => {
      if (prev != null) meta?.setAttribute('content', prev);
      if (prevCanon != null) canonical?.setAttribute('href', prevCanon);
      else document.title = 'WebCodeCraft | Devon web design — no upfront cost';
    };
  }, []);

  return (
    <>
      <Header />
      <main
        id="main-content"
        className="pt-24 pb-16 min-w-0 overflow-x-clip bg-gradient-to-b from-gray-50 to-white min-h-screen"
      >
        <div className="container mx-auto px-4 max-w-3xl">
          <m.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sm text-indigo-600 font-medium mb-4">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <span className="text-gray-600">Guides</span>
            </p>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Guides — SEO, accessibility &amp; smarter websites
            </h1>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              Short, practical articles for UK businesses — from search basics to accessibility and long-term
              technology choices. Every guide links back to our tools and contact options when you want help.
            </p>

            <ul className="space-y-4 mb-12">
              <li>
                <Link
                  to={`/blog/${BLOG_SLUG_FREE_WEBSITE_UK}`}
                  className="block rounded-xl border border-gray-200 bg-white p-5 hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors"
                >
                  <span className="font-semibold text-indigo-700">
                    Free websites in the UK vs builders — what &quot;free&quot; really means
                  </span>
                  <span className="block text-sm text-gray-600 mt-1">
                    DIY platforms vs custom development, and how WebCodeCraft fits in.
                  </span>
                </Link>
              </li>
              {GUIDE_ARTICLES.map((g) => (
                <li key={g.slug}>
                  <Link
                    to={`/guides/${g.slug}`}
                    className="block rounded-xl border border-gray-200 bg-white p-5 hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors"
                  >
                    <span className="font-semibold text-indigo-700">{g.title}</span>
                    <span className="block text-sm text-gray-600 mt-1">{g.description}</span>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-6 text-slate-700 text-sm space-y-3">
              <p className="font-medium text-slate-900">Tools that pair with these guides</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <Link to="/check-your-seo" className="text-indigo-600 font-medium hover:underline">
                    Free SEO audit
                  </Link>
                </li>
                <li>
                  <Link to="/accessibility-check" className="text-indigo-600 font-medium hover:underline">
                    Accessibility scan
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="text-indigo-600 font-medium hover:underline">
                    Services &amp; pricing
                  </Link>
                </li>
              </ul>
              <p>
                <a href={internalUrl('contact')} className="text-indigo-600 font-semibold hover:underline">
                  Contact us
                </a>{' '}
                for a project conversation — no obligation.
              </p>
            </div>
          </m.div>
        </div>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default GuidesHubPage;
